<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useStore } from 'dashboard/composables/store';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { checklistCount, checklistFor, greetingKey } from '../visorSteps';
import {
  BTN,
  BTN_LINK,
  BTN_SMALL,
  CARD,
  CHIP,
  CHIP_TONES,
  ENTER,
  FIELD,
  H1,
  MOTION,
  SMALL,
  SUB,
  TILE,
  TILE_TONES,
} from '../ui';

// Painel inicial: números de EXEMPLO marcados como exemplo enquanto a primeira
// conversa não chega, e checklist de 6 passos com um único próximo passo.
const props = defineProps({
  state: { type: Object, required: true },
  properties: { type: Object, default: null },
  finishing: { type: Boolean, default: false },
});
const emit = defineEmits(['finish', 'goTo', 'refresh']);

const { t } = useI18n();
const store = useStore();

const facts = computed(() => props.state.facts || {});
const realEstate = computed(() => Boolean(props.state.real_estate));
const variant = computed(() => (realEstate.value ? 'REAL_ESTATE' : 'BASE'));
const shortName = computed(() =>
  (facts.value.name?.value || '').split(' ').slice(0, 2).join(' ')
);
const items = computed(() => checklistFor(props.state));
const doneCount = computed(() => checklistCount(items.value));
const next = computed(() => items.value.find(item => item.next));
const showExample = computed(() => !props.state.has_conversations);

const ITEM_ICONS = {
  conta: 'i-ph-identification-card-duotone',
  dados: 'i-ph-broadcast-duotone',
  aparencia: 'i-ph-palette-duotone',
  contas: 'i-ph-link-duotone',
  whatsapp: 'i-ri-whatsapp-fill',
  imoveis: 'i-ph-buildings-duotone',
  primeira_conversa: 'i-ph-trophy-duotone',
  equipe: 'i-ph-users-three-duotone',
};

const firstProperty = computed(() => {
  const list = props.properties?.items || [];
  const checked = props.state.progress?.checked_properties || [];
  return list.find(item => checked.includes(item.code) && item.url) || list.find(item => item.url);
});
const copied = ref(false);
const copyLink = async () => {
  const text = realEstate.value ? firstProperty.value?.url : facts.value.whatsapp?.value;
  if (!text) return;
  await copyTextToClipboard(text);
  copied.value = true;
};

const inviteEmail = ref('');
const inviting = ref(false);
const invited = ref(false);
const inviteFailed = ref(false);
const invite = async () => {
  const email = inviteEmail.value.trim();
  if (!email) return;
  inviting.value = true;
  inviteFailed.value = false;
  try {
    await store.dispatch('agents/create', { name: email.split('@')[0], email, role: 'agent' });
    inviteEmail.value = '';
    invited.value = true;
    emit('refresh');
  } catch {
    inviteFailed.value = true;
  } finally {
    inviting.value = false;
  }
};

const KPIS = [
  { key: 'CONVERSATIONS', icon: 'i-ph-chat-circle-dots-duotone' },
  { key: 'AI_ANSWERED', icon: 'i-ph-magic-wand-duotone' },
  { key: 'THIRD', icon: 'i-ph-calendar-duotone' },
  { key: 'TEAM', icon: 'i-ph-user-duotone' },
];
const CONVERSATIONS = [
  { icon: 'i-ri-whatsapp-fill', tone: CHIP_TONES.azul, time: '2 min' },
  { icon: 'i-ri-instagram-fill', tone: CHIP_TONES.ok, time: '18 min' },
  { icon: 'i-ri-whatsapp-fill', tone: CHIP_TONES.azul, time: '1 h' },
  { icon: 'i-ri-facebook-circle-fill', tone: CHIP_TONES.aviso, time: '3 h' },
];
const FUNNEL = [32, 18, 7, 2];

const tips = computed(() => [
  t(`ONBOARDING_VISOR.PANEL.TIPS.${variant.value}.0`),
  t(`ONBOARDING_VISOR.PANEL.TIPS.${variant.value}.1`),
]);
const tipIndex = ref(0);
</script>

<template>
  <div>
    <section class="flex gap-6 items-center max-md:flex-col max-md:items-start max-md:gap-3" :class="ENTER">
      <img
        v-if="facts.logo_url?.value"
        :src="facts.logo_url.value"
        alt=""
        class="size-16 rounded-2xl object-contain bg-white flex-none max-md:size-12"
      />
      <div>
        <h1 :class="H1">
          <span class="text-n-slate-10">
            {{ t(`ONBOARDING_VISOR.PANEL.GREETING.${greetingKey()}`, { name: shortName }) }}
          </span>
          <br />
          {{ t('ONBOARDING_VISOR.PANEL.TITLE') }}
        </h1>
        <p :class="SUB">
          {{ showExample ? t('ONBOARDING_VISOR.PANEL.SUBTITLE') : t('ONBOARDING_VISOR.PANEL.SUBTITLE_LIVE') }}
        </p>
      </div>
      <button type="button" :class="BTN" class="md:ml-auto max-md:w-full" :disabled="finishing" data-testid="abrir-crm" @click="emit('finish')">
        {{ t('ONBOARDING_VISOR.PANEL.OPEN_CRM') }}
        <Icon icon="i-ph-arrow-right-duotone" class="size-5" />
      </button>
    </section>

    <div
      v-if="showExample"
      class="flex items-center gap-2.5 mt-6 px-4 py-3 rounded-xl bg-n-violet-3 text-n-slate-12 text-sm"
      data-testid="aviso-exemplo"
    >
      <Icon icon="i-ph-info-duotone" class="size-5 text-n-violet-11 flex-none" />
      <span><b class="font-medium">{{ t('ONBOARDING_VISOR.PANEL.EXAMPLE_NOTICE_BOLD') }}</b> {{ t('ONBOARDING_VISOR.PANEL.EXAMPLE_NOTICE') }}</span>
    </div>

    <div class="grid gap-6 mt-6 xl:grid-cols-[25rem_minmax(0,1fr)] items-start max-md:gap-4">
      <section class="rounded-[1.25rem] p-5 text-white bg-[linear-gradient(180deg,#1A6DF5,#0147E7)]" data-testid="checklist">
        <div class="flex items-center gap-2.5 mb-3.5 font-medium">
          <Icon icon="i-ph-flag-duotone" class="size-5" />
          {{ t('ONBOARDING_VISOR.PANEL.CHECKLIST_TITLE') }}
          <span class="ml-auto text-sm opacity-90" data-testid="checklist-contagem">
            {{ t('ONBOARDING_VISOR.PANEL.CHECKLIST_COUNT', { n: doneCount, total: items.length }) }}
          </span>
        </div>
        <div class="h-2 rounded-full bg-white/30 mb-4 overflow-hidden">
          <i class="block h-full bg-white rounded-full" :class="MOTION" :style="{ width: `${(doneCount / items.length) * 100}%` }" />
        </div>
        <div class="bg-n-card text-n-slate-12 rounded-2xl p-4">
          <ul class="grid gap-0.5">
            <li
              v-for="item in items"
              :key="item.key"
              class="flex items-center gap-3 py-2 text-sm"
              :class="item.done ? 'text-n-slate-10 line-through decoration-n-slate-10/50' : item.next ? 'text-n-slate-12 font-medium' : 'text-n-slate-11'"
              :data-testid="`item-${item.key}`"
              :data-state="item.done ? 'feito' : item.next ? 'proximo' : 'pendente'"
            >
              <span
                class="size-7 rounded-lg grid place-items-center flex-none"
                :class="item.done ? 'bg-n-teal-3 text-n-teal-11' : item.next ? 'bg-n-blue-9 text-white' : 'bg-n-slate-3 text-n-slate-10'"
              >
                <Icon :icon="item.done ? 'i-ph-check-circle-duotone' : ITEM_ICONS[item.key]" class="size-4" />
              </span>
              <span>{{ t(`ONBOARDING_VISOR.PANEL.ITEMS.${item.key.toUpperCase()}`) }}</span>
              <span v-if="item.optional && !item.done && !item.next" class="ml-auto no-underline" :class="SMALL">
                {{ t('ONBOARDING_VISOR.PANEL.OPTIONAL') }}
              </span>
            </li>
          </ul>

          <div v-if="next" class="mt-3 p-4 rounded-xl bg-n-blue-3" data-testid="proximo-passo">
            <p class="text-[0.8125rem] text-n-blue-11 font-medium">{{ t('ONBOARDING_VISOR.PANEL.NEXT_STEP') }}</p>
            <h3 class="font-medium mt-0.5">{{ t(`ONBOARDING_VISOR.PANEL.NEXT.${next.key.toUpperCase()}.TITLE`) }}</h3>
            <p class="text-sm text-n-slate-11 mt-1.5">
              {{ t(`ONBOARDING_VISOR.PANEL.NEXT.${next.key.toUpperCase()}.TEXT_${next.key === 'primeira_conversa' ? variant : 'ALL'}`) }}
            </p>
            <div class="flex flex-wrap gap-2 mt-3.5">
              <button v-if="next.step" type="button" :class="[BTN, BTN_SMALL]" @click="emit('goTo', next.step)">
                {{ t(`ONBOARDING_VISOR.PANEL.NEXT.${next.key.toUpperCase()}.ACTION`) }}
              </button>
              <template v-else-if="next.key === 'primeira_conversa'">
                <button
                  v-if="realEstate ? firstProperty : facts.whatsapp?.value"
                  type="button"
                  :class="[BTN, BTN_SMALL]"
                  class="max-md:w-full max-md:whitespace-normal"
                  @click="copyLink"
                >
                  <Icon icon="i-ph-copy-duotone" class="size-5" />
                  {{
                    copied
                      ? t('ONBOARDING_VISOR.PANEL.COPIED')
                      : realEstate
                        ? t('ONBOARDING_VISOR.PANEL.COPY_PROPERTY', { title: firstProperty.title })
                        : t('ONBOARDING_VISOR.PANEL.COPY_WHATSAPP')
                  }}
                </button>
                <button v-if="realEstate" type="button" :class="[BTN_LINK, BTN_SMALL]" @click="emit('goTo', 'teste_ia')">
                  {{ t('ONBOARDING_VISOR.PANEL.SEE_TEST') }}
                </button>
              </template>
            </div>
          </div>

          <form class="mt-3 grid gap-2" data-testid="convidar" @submit.prevent="invite">
            <label for="onboarding-invite" class="text-[0.8125rem] text-n-slate-11">
              {{ t('ONBOARDING_VISOR.PANEL.INVITE_LABEL') }}
            </label>
            <div class="flex gap-2">
              <input
                id="onboarding-invite"
                v-model="inviteEmail"
                type="email"
                :class="FIELD"
                class="flex-1 min-w-0 !mb-0"
                :placeholder="t('ONBOARDING_VISOR.PANEL.INVITE_PLACEHOLDER')"
              />
              <button type="submit" :class="[BTN, BTN_SMALL]" class="!h-12" :disabled="inviting || !inviteEmail.trim()">
                {{ t('ONBOARDING_VISOR.PANEL.INVITE') }}
              </button>
            </div>
            <p v-if="invited" class="text-sm text-n-teal-11">{{ t('ONBOARDING_VISOR.PANEL.INVITED') }}</p>
            <p v-if="inviteFailed" class="text-sm text-n-amber-11">{{ t('ONBOARDING_VISOR.PANEL.INVITE_ERROR') }}</p>
          </form>
        </div>
        <div class="mt-4 min-h-11 text-sm">
          <p>{{ tips[tipIndex] }}</p>
          <div class="flex gap-1.5 mt-3.5">
            <button
              v-for="(_, index) in tips"
              :key="index"
              type="button"
              class="h-1.5 rounded-full"
              :class="[MOTION, index === tipIndex ? 'w-[1.125rem] bg-white' : 'w-1.5 bg-white/45']"
              :aria-label="t('ONBOARDING_VISOR.TIP_N', { n: index + 1 })"
              @click="tipIndex = index"
            />
          </div>
        </div>
      </section>

      <div v-if="showExample" class="grid gap-6 min-w-0 max-md:gap-4">
        <div class="grid grid-cols-4 gap-6 max-xl:grid-cols-2 max-md:gap-3">
          <section v-for="kpi in KPIS" :key="kpi.key" :class="CARD" class="!p-5 max-md:!p-4" data-testid="kpi-exemplo">
            <div class="flex justify-between items-center mb-3.5">
              <span :class="[TILE, TILE_TONES.neutro]"><Icon :icon="kpi.icon" /></span>
              <span :class="[CHIP, CHIP_TONES.exemplo]">{{ t('ONBOARDING_VISOR.PANEL.EXAMPLE') }}</span>
            </div>
            <p :class="SMALL">{{ t(`ONBOARDING_VISOR.PANEL.KPIS.${variant}.${kpi.key}.LABEL`) }}</p>
            <p class="text-[2rem] font-medium tracking-[-0.03em] text-n-slate-11 leading-tight my-0.5 max-md:text-[1.625rem]">
              {{ t(`ONBOARDING_VISOR.PANEL.KPIS.${variant}.${kpi.key}.VALUE`) }}
            </p>
            <p :class="SMALL">{{ t(`ONBOARDING_VISOR.PANEL.KPIS.${variant}.${kpi.key}.DETAIL`) }}</p>
          </section>
        </div>

        <div class="grid gap-6 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] items-start max-md:gap-4">
          <section :class="CARD">
            <div class="flex items-center gap-3 mb-4">
              <span :class="[TILE, TILE_TONES.neutro]"><Icon icon="i-ph-chat-circle-dots-duotone" /></span>
              <h3 class="font-medium">{{ t('ONBOARDING_VISOR.PANEL.RECENT') }}</h3>
              <span :class="[CHIP, CHIP_TONES.exemplo]">{{ t('ONBOARDING_VISOR.PANEL.EXAMPLE') }}</span>
            </div>
            <ul>
              <li
                v-for="(conversation, index) in CONVERSATIONS"
                :key="index"
                class="flex items-center gap-3 py-3 border-t border-n-weak first:border-t-0 first:pt-0"
              >
                <span class="size-9 rounded-full grid place-items-center bg-n-slate-3 text-n-slate-10 font-medium flex-none">
                  {{ t(`ONBOARDING_VISOR.PANEL.CONVERSATIONS.${variant}.${index}.NAME`).charAt(0) }}
                </span>
                <div class="min-w-0 flex-1">
                  <b class="font-medium">
                    {{ t(`ONBOARDING_VISOR.PANEL.CONVERSATIONS.${variant}.${index}.NAME`) }}
                    <span :class="SMALL" class="font-normal">{{ t('ONBOARDING_VISOR.PANEL.EXAMPLE_TAG') }}</span>
                  </b>
                  <p class="text-sm text-n-slate-11 truncate">
                    {{ t(`ONBOARDING_VISOR.PANEL.CONVERSATIONS.${variant}.${index}.TEXT`) }}
                  </p>
                </div>
                <Icon :icon="conversation.icon" class="size-[1.125rem] text-n-slate-10 flex-none" />
                <div class="flex flex-col items-end gap-1">
                  <span :class="SMALL">{{ conversation.time }}</span>
                  <span :class="[CHIP, conversation.tone]">
                    {{ t(`ONBOARDING_VISOR.PANEL.CONVERSATIONS.${variant}.${index}.STATUS`) }}
                  </span>
                </div>
              </li>
            </ul>
          </section>

          <section :class="CARD">
            <div class="flex items-center gap-3 mb-4">
              <span :class="[TILE, TILE_TONES.neutro]"><Icon icon="i-ph-chart-bar-duotone" /></span>
              <h3 class="font-medium">{{ t('ONBOARDING_VISOR.PANEL.FUNNEL') }}</h3>
              <span :class="[CHIP, CHIP_TONES.exemplo]">{{ t('ONBOARDING_VISOR.PANEL.EXAMPLE') }}</span>
            </div>
            <div class="grid gap-3">
              <div
                v-for="(value, index) in FUNNEL"
                :key="index"
                class="grid grid-cols-[7rem_1fr_1.75rem] gap-3 items-center text-sm text-n-slate-11"
              >
                <span>{{ t(`ONBOARDING_VISOR.PANEL.FUNNEL_STAGES.${variant}.${index}`) }}</span>
                <div class="h-2.5 rounded-full bg-n-slate-3 overflow-hidden">
                  <i class="block h-full rounded-full bg-n-violet-9 opacity-55" :style="{ width: `${(value / FUNNEL[0]) * 100}%` }" />
                </div>
                <b class="text-right font-medium">{{ value }}</b>
              </div>
            </div>
            <template v-if="realEstate && properties?.items?.length">
              <div class="flex items-center gap-3 mt-6 mb-3">
                <span :class="[TILE, TILE_TONES.ok]"><Icon icon="i-ph-buildings-duotone" /></span>
                <h3 class="font-medium">{{ t('ONBOARDING_VISOR.PANEL.TOP_PROPERTIES') }}</h3>
              </div>
              <ul class="grid gap-3">
                <li v-for="(property, index) in properties.items.slice(0, 2)" :key="property.code" class="flex gap-3 items-center">
                  <img v-if="property.photo" :src="property.photo" alt="" class="w-14 h-11 rounded-lg object-cover flex-none" />
                  <div>
                    <b class="font-medium">{{ property.title }}</b>
                    <p :class="SMALL">{{ t('ONBOARDING_VISOR.PANEL.TOP_PROPERTY_HINT', { n: [8, 5][index] }) }}</p>
                  </div>
                </li>
              </ul>
            </template>
          </section>
        </div>
      </div>

      <section v-else :class="CARD" data-testid="primeira-chegou">
        <div class="flex items-center gap-3 mb-3">
          <span :class="[TILE, TILE_TONES.ok]"><Icon icon="i-ph-chat-circle-dots-duotone" /></span>
          <h2 class="text-xl font-medium tracking-[-0.02em]">{{ t('ONBOARDING_VISOR.PANEL.LIVE_TITLE') }}</h2>
        </div>
        <p class="text-n-slate-11">{{ t('ONBOARDING_VISOR.PANEL.LIVE_TEXT') }}</p>
      </section>
    </div>
  </div>
</template>
